/**
 * Familienkonto — Detail
 *
 * Obere schmale Leiste: Zurück + Konto-Kontext (Name/Notiz editierbar) +
 * Personen-Umschalter (members mit Rolle-Badge, aktive Person markiert) +
 * „+ Person hinzufügen".
 *
 * Hauptbereich: die VOLLSTÄNDIGE `PbDetailPage` der aktiven Person — also die
 * gewohnte PB-Profilansicht (PbSidebar + Tab-Navigation + Dashboard-Widget-
 * Grid). Jede Person ist ein eigener User; ihr `V1User` wird via
 * `v1Users.get(member.user_id)` geladen und an `PbDetailPage` gereicht.
 * PbDetailPage/PbSidebar/Tabs werden NICHT neu gebaut — nur wiederverwendet.
 *
 * „+ Person hinzufügen" nutzt das schlanke `FamilyMemberForm` (Schnell-Anlegen
 * = user+profil via v2Members.create). Bestehende Personen pflegt man über den
 * Profildaten-Tab der PbDetailPage.
 *
 * Kontaktpersonen (kein Pflegeempfänger) bekommen denselben Profilaufbau —
 * `careRecipient={false}` blendet nur die betreuungsbezogenen Teile aus
 * (Tabs Tagesplan/Betreuungen/Medien & Dokumente/Bewertungen, Sidebar-Blöcke
 * Personenangaben/Startdatum/Nettolohn, Dashboard-Jahresübersicht +
 * Betreuungen-Widget). Früher sahen sie nur das schlanke `FamilyMemberForm`;
 * das bleibt der Fallback für Alt-Kontakte ohne eigenen users-Datensatz.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Card } from '../../../../components/ui';
import { Icon } from '../../../../components/icons';
import { getThemeColors } from '../../../../lib/theme';
import {
  v2FamilyAccounts, v2Members, isCareRecipientType,
  type FamilyAccount, type FamilyMember,
} from '../../../../lib/familyAccounts';
import { v1ProfileOptions, v1Users, type V1ProfileOption, type V1User } from '../../../../lib/v1';
import { labelOptions } from '../../profile/options';
import { useOptionLabel } from '../../../../lib/useOptionLabel';
import { FamilyMemberForm } from '../FamilyMemberForm';
import { PbDetailPage, type PbTabId } from '../../PbDetailPage';
import { type Props } from './types';
import { BackBar } from './BackBar';
import { AccountForm } from './AccountForm';
import { AccountEditToggle } from './AccountEditToggle';
import { useNavSlots } from '../../../../components/shell/ProfileNavContext';
import { useIsMobile } from '../../../../lib/useBreakpoint';

export type { Props };

function resolveRoleLabel(
  profiltyp: string | null,
  options: Array<{ value: string; label: string }>,
): string {
  if (!profiltyp) return '—';
  return options.find(o => o.value === profiltyp)?.label ?? profiltyp;
}

export function FamilienkontoDetailPage({
  isNew = false, accountId, userId, activeTab, dark, onBack, onOpenAccount,
  onSelectMember, onSelectMemberTab,
}: Props) {
  const t = getThemeColors(dark);
  const { t: tr } = useTranslation();
  const optLabel = useOptionLabel();
  const isMobile = useIsMobile();

  // Konto-ID ggf. selbst aus `userId` auflösen (kanonische Route
  // /pflege/{userId} liefert nur die users.id, nicht das Familienkonto).
  // Bevorzugt aus bereits geladenen `account.members`, damit ein Wechsel
  // zwischen Mitgliedern DESSELBEN Kontos (Chip-Klick) keinen Re-Fetch/
  // Remount des Konto-Umschalters auslöst — nur ein echter Konto-Wechsel
  // (z. B. Deep-Link/globale Suche auf eine andere Familie) fragt
  // `v1Users.get` erneut ab.
  const [resolvedAccountId, setResolvedAccountId] = useState<number | null>(null);
  const [resolveError, setResolveError] = useState(false);

  const [account, setAccount] = useState<FamilyAccount | null>(null);
  const effectiveAccountId = accountId ?? resolvedAccountId;
  const [loading, setLoading] = useState(effectiveAccountId != null);
  const [error, setError] = useState<string | null>(null);
  const [opts, setOpts] = useState<Record<string, V1ProfileOption[]> | null>(null);
  /** true = „Person hinzufügen"-Formular offen (statt aktive Person zeigen). */
  const [adding, setAdding] = useState(false);
  /** Geladener V1User der aktiven Person (für PbDetailPage). */
  const [activeUser, setActiveUser] = useState<V1User | null>(null);
  const [userLoading, setUserLoading] = useState(false);
  /**
   * Manuelle Auswahl EINER Kontaktperson ohne eigenen users-Datensatz (Alt-
   * Kontakt) — die hat kein `userId` und kann darum nicht über die URL
   * (/pflege/{userId}) referenziert werden. Ein Chip-Klick auf so eine
   * Person setzt diesen lokalen Override statt zu navigieren; jeder
   * echte Routenwechsel (anderer `userId`/`accountId`) setzt ihn zurück.
   */
  const [manualMemberOverride, setManualMemberOverride] = useState<FamilyMember | null>(null);
  useEffect(() => { setManualMemberOverride(null); }, [userId, accountId]);

  // Options laden (für Rolle-Badges im Umschalter).
  useEffect(() => {
    let alive = true;
    v1ProfileOptions.grouped().then(g => { if (alive) setOpts(g); }).catch(() => {});
    return () => { alive = false; };
  }, []);

  // userId → family_account_id auflösen (nur wenn accountId nicht schon
  // direkt bekannt ist, s. Props-Doku).
  useEffect(() => {
    setResolveError(false);
    if (accountId != null || userId == null) { setResolvedAccountId(null); return; }
    // Schon geladenes Konto enthält diese Person bereits (Mitglieder-
    // Wechsel innerhalb desselben Kontos) → kein zusätzlicher Request.
    if (account && account.members.some(m => m.user_id === userId)) {
      setResolvedAccountId(account.id);
      return;
    }
    let alive = true;
    v1Users.get(userId)
      .then(u => {
        if (!alive) return;
        const raw = (u as unknown as { family_account_id?: number | string | null }).family_account_id;
        const famId =
          typeof raw === 'number' ? raw
          : typeof raw === 'string' && /^\d+$/.test(raw) ? Number(raw)
          : null;
        if (famId == null) { setResolveError(true); setResolvedAccountId(null); return; }
        setResolvedAccountId(famId);
      })
      .catch(() => { if (alive) setResolveError(true); });
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, accountId]);

  // Konto laden.
  useEffect(() => {
    if (effectiveAccountId == null) { setAccount(null); setLoading(false); return; }
    let alive = true;
    setLoading(true);
    setError(null);
    v2FamilyAccounts.get(effectiveAccountId)
      .then(a => { if (alive) setAccount(a); })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : tr('pflege.familienkonto.detail.account_load_error')); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [effectiveAccountId, tr]);

  // Label-basierte Options (value = Label), da m.profiltyp vom Server das
  // Label trägt — so matchen resolveRoleLabel + isCareRecipientType korrekt.
  const profiltypOptions = useMemo(() => labelOptions('profiltyp', opts), [opts]);

  const members = account?.members ?? [];

  // Person löschen (✕ am aktiven Chip → Bestätigungsdialog).
  const [deleteTarget, setDeleteTarget] = useState<FamilyMember | null>(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Ehemals durch `key={accountId}` in App.tsx erzwungener Remount bei
  // Konto-Wechsel — seit der Umstellung auf /pflege/{userId} gibt es
  // absichtlich KEIN `key` mehr (sonst würde jeder Mitglieder-Wechsel
  // innerhalb desselben Kontos die Mitgliederliste neu laden/flackern
  // lassen, s. App.tsx). Transiente UI-Zustände müssen sich darum bei einem
  // ECHTEN Kontowechsel selbst zurücksetzen.
  useEffect(() => {
    setAdding(false);
    setDeleteTarget(null);
    setDeleteError(null);
  }, [effectiveAccountId]);

  const confirmDelete = async () => {
    if (!deleteTarget || deleting || account == null) return;
    setDeleting(true);
    setDeleteError(null);
    try {
      await v2Members.remove(deleteTarget.profile_id);
      const remaining = members.filter(m => m.profile_id !== deleteTarget.profile_id);
      setAccount({
        ...account,
        members: remaining,
        members_count: remaining.length,
        care_recipients: remaining
          .filter(m => m.is_care_recipient || m.is_pflegeempfaenger)
          .map(m => m.fullname)
          .filter((n): n is string => !!n),
      });
      // War die gelöschte Person aktiv → zur ersten verbleibenden wechseln.
      if (activeMember?.profile_id === deleteTarget.profile_id && remaining.length > 0 && effectiveAccountId != null) {
        onSelectMember(remaining[0].user_id, effectiveAccountId, remaining[0].profile_id);
      }
      setDeleteTarget(null);
    } catch {
      setDeleteError(tr('pflege.familienkonto.detail.delete_member_error'));
    } finally {
      setDeleting(false);
    }
  };

  // Pflegeempfänger des Kontos — Ziel für "Anfrage abbrechen" im ⋯-Menü UND
  // Default-Mitglied, wenn das Konto ohne explizite profileId geöffnet wird
  // (s. activeMember unten).
  const peMember: FamilyMember | null = useMemo(() =>
    members.find(m => m.is_pflegeempfaenger || m.is_care_recipient || isCareRecipientType(m.profiltyp, profiltypOptions)) ?? null,
  [members, profiltypOptions]);

  const activeMember: FamilyMember | null = useMemo(() => {
    // Alt-Kontakt ohne eigenen users-Datensatz, lokal per Chip gewählt (s.
    // manualMemberOverride oben) — hat Vorrang, solange die Route sich nicht
    // ändert (der Reset-Effect löscht den Override dann wieder).
    if (manualMemberOverride != null) return manualMemberOverride;
    if (userId != null) return members.find(m => m.user_id === userId) ?? null;
    // Hauptkontakt ist immer der Pflegeempfänger: ohne explizite userId
    // (Konto-only-Ansicht /pflege/konto/{id}) NICHT einfach das erste
    // Mitglied der Liste zeigen — das kann je nach Anlege-Reihenfolge eine
    // Kontaktperson sein (z. B. "Max junior"). Fallback auf members[0] nur,
    // wenn kein Mitglied als Pflegeempfänger markiert ist (Altdaten).
    return peMember ?? members[0] ?? null;
  }, [members, userId, peMember, manualMemberOverride]);

  // V1User der aktiven Person laden (für die vollständige PbDetailPage).
  useEffect(() => {
    if (adding) return;
    const uid = activeMember?.user_id;
    if (uid == null) { setActiveUser(null); return; }
    let alive = true;
    setUserLoading(true);
    v1Users.get(uid)
      .then(u => { if (alive) setActiveUser(u); })
      .catch(e => { if (alive) setError(e instanceof Error ? e.message : tr('pflege.familienkonto.detail.person_load_error')); })
      .finally(() => { if (alive) setUserLoading(false); });
    return () => { alive = false; };
  }, [activeMember?.user_id, adding, tr]);

  const reloadAccount = async () => {
    if (effectiveAccountId == null) return;
    const a = await v2FamilyAccounts.get(effectiveAccountId);
    setAccount(a);
    return a;
  };

  // „‹ Zurück zu den Konten" in die globale Navbar heben. Kontoname erscheint
  // nicht mehr in der Navbar; „Konto bearbeiten" lebt als Zahnrad in der
  // Personen-Zeile.
  // Kunde = erscheint unter /pflege (letzte aktive Stage + bestätigte Care,
  // Backend User::scopeKunde → Member.is_kunde). Bis dahin ist das Konto ein
  // Lead und gehört im Menü zu „Anfragen". Solange das Konto lädt (oder ein
  // älteres Backend is_kunde nicht liefert), bleibt der Menüpunkt aus der URL.
  const kundeKnown = members.some(m => typeof m.is_kunde === 'boolean');
  const isKunde = members.some(m => m.is_kunde === true);
  const activeMenu = kundeKnown && !isKunde ? 'anfragen' : null;

  useNavSlots(
    effectiveAccountId != null
      ? {
          leading: (
            <BackBar dark={dark} onBack={onBack} label={tr('pflege.familienkonto.detail.back')} compact={isMobile} />
          ),
          activeMenu,
        }
      : { leading: null, trailing: null, activeMenu: null },
    [effectiveAccountId, dark, activeMenu, isMobile],
  );

  const handleMemberAdded = async (m: FamilyMember) => {
    setAdding(false);
    const a = await reloadAccount();
    if (a && effectiveAccountId != null) onSelectMember(m.user_id, effectiveAccountId, m.profile_id);
  };

  // ── „Neues Konto" — erst Konto anlegen ──
  if (isNew) {
    return (
      <div style={{ padding: 24, maxWidth: 720, margin: '0 auto' }}>
        <BackBar dark={dark} onBack={onBack} label={tr('pflege.familienkonto.detail.back')} />
        <h2 style={{ fontSize: 20, fontWeight: 700, color: t.heading, margin: '14px 0 4px' }}>
          {tr('pflege.familienkonto.detail.new_title')}
        </h2>
        <div style={{ fontSize: 13, color: t.muted, marginBottom: 18 }}>
          {tr('pflege.familienkonto.detail.create_first_hint')}
        </div>
        <AccountForm dark={dark} account={null} onSaved={acc => onOpenAccount(acc.id)} />
      </div>
    );
  }

  // userId aus der kanonischen Route (/pflege/{userId}) konnte nicht auf ein
  // Familienkonto aufgelöst werden (User ohne family_account_id, gelöscht,
  // oder Request fehlgeschlagen) — kein Endlos-Ladezustand.
  if (resolveError) {
    return <div style={{ padding: 24, color: '#b42318', fontSize: 13 }}>{tr('pflege.detail.not_found')}</div>;
  }

  if (loading || effectiveAccountId == null) {
    return <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('pflege.familienkonto.list.loading')}</div>;
  }

  return (
    // Mobil: kein eigenes Padding — `.app-main` polstert dort schon 14px,
    // zusammen blieben bei 344px Breite nur ~270px für den Inhalt.
    <div style={{ display: 'flex', flexDirection: 'column', height: isMobile ? 'auto' : '100%', padding: isMobile ? 0 : 24, minHeight: 0 }}>
      {/* Obere Leiste: Personen-Umschalter (Zurück/Kontoname/Bearbeiten leben
          jetzt in der globalen Navbar — siehe useNavSlots oben). */}
      <div style={{ flexShrink: 0, display: 'flex', flexDirection: 'column', gap: 12, marginBottom: 16 }}>
        {/* Personen-Umschalter (Chips) + „+ Person hinzufügen" */}
        <div role="tablist" aria-label={tr('pflege.familienkonto.detail.members_section')} style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          {members.map(m => {
            const on = !adding && activeMember?.profile_id === m.profile_id;
            const roleLabel = m.profiltypen && m.profiltypen.length > 1
              ? m.profiltypen.map(r => optLabel(resolveRoleLabel(r, profiltypOptions))).join(', ')
              : optLabel(resolveRoleLabel(m.profiltyp, profiltypOptions));
            const isCare = m.is_pflegeempfaenger || m.is_care_recipient || isCareRecipientType(m.profiltyp, profiltypOptions);
            return (
              <button
                key={m.profile_id}
                type="button"
                role="tab"
                aria-selected={on}
                onClick={() => {
                  setAdding(false);
                  // Alt-Kontakt ohne eigenen users-Datensatz: kein /pflege/{userId}
                  // möglich → lokale Auswahl statt Navigation (s. manualMemberOverride).
                  if (m.user_id == null) { setManualMemberOverride(m); return; }
                  if (effectiveAccountId != null) onSelectMember(m.user_id, effectiveAccountId, m.profile_id);
                }}
                style={{
                  display: 'inline-flex', alignItems: 'center', gap: 8,
                  // Chip darf nie breiter als die Zeile werden (Handy): der
                  // Name kürzt sich per Ellipse, Rolle + ✕ bleiben sichtbar.
                  maxWidth: '100%', minWidth: 0,
                  border: `1px solid ${on ? '#b5297a' : t.border}`, borderRadius: 9999,
                  padding: '6px 12px', cursor: 'pointer', fontFamily: 'Outfit,sans-serif',
                  background: on ? (dark ? '#2a1222' : '#fce7f4') : t.card,
                  color: on ? '#b5297a' : t.text, fontSize: 13, fontWeight: on ? 600 : 500,
                }}
              >
                <span style={{ maxWidth: 180, minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                  {m.fullname || [m.vorname, m.nachname].filter(Boolean).join(' ') || `#${m.profile_id}`}
                </span>
                <span style={{
                  fontSize: 10.5, fontWeight: 600, borderRadius: 999, padding: '1px 7px',
                  flexShrink: 0, whiteSpace: 'nowrap',
                  background: isCare ? (dark ? '#3a1830' : '#f9d4e9') : (dark ? '#1e2735' : '#f2f4f7'),
                  color: isCare ? '#b5297a' : t.muted,
                }} title={roleLabel}>
                  {/* Der Profiltyp ist die eigentliche Information („Hauptkontakt",
                      „Rechnungsempfänger", …); „Kontakt" bleibt nur der Notnagel,
                      wenn am Mitglied kein Profiltyp hinterlegt ist. */}
                  {isCare
                    ? tr('pflege.familienkonto.detail.role_care_recipient')
                    : (roleLabel || tr('pflege.familienkonto.detail.role_contact'))}
                </span>
                {/* Löschen: nur am aktiven Chip, und nie für die letzte Person */}
                {on && members.length > 1 && (
                  <span
                    role="button"
                    aria-label={tr('pflege.familienkonto.detail.delete_member')}
                    title={tr('pflege.familienkonto.detail.delete_member')}
                    onClick={e => { e.stopPropagation(); setDeleteError(null); setDeleteTarget(m); }}
                    style={{
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                      width: 16, height: 16, borderRadius: 999, fontSize: 13, lineHeight: 1, flexShrink: 0,
                      color: '#b5297a', cursor: 'pointer',
                    }}
                  >×</span>
                )}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setAdding(true)}
            style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              border: `1px dashed ${t.border}`, borderRadius: 9999, padding: '6px 12px',
              cursor: 'pointer', background: 'transparent', color: '#b5297a',
              fontSize: 13, fontWeight: 600, fontFamily: 'Outfit,sans-serif',
            }}
          >
            <Icon.Plus c="#b5297a" size={13} /> {tr('pflege.familienkonto.detail.add_member')}
          </button>
          {/* ⋯-Menü: Familienkonto bearbeiten (Drawer) + Anfrage abbrechen —
              direkt in der Personen-Zeile (ehemals Zahnrad). */}
          <AccountEditToggle
            dark={dark}
            account={account}
            onSaved={setAccount}
            opts={opts}
            peUserId={peMember?.user_id ?? null}
            peName={peMember?.fullname || [peMember?.vorname, peMember?.nachname].filter(Boolean).join(' ') || undefined}
            pePhase={activeUser?.id != null && activeUser.id === peMember?.user_id && typeof activeUser.phase === 'number' ? activeUser.phase : undefined}
            onCancelled={r => {
              if (activeUser?.id === peMember?.user_id && activeUser) {
                setActiveUser({ ...activeUser, phase: r.phase } as V1User);
              }
            }}
            onRestored={r => {
              // Batch-12: Phase zurück auf den Stand vor dem Abbruch.
              if (activeUser?.id === peMember?.user_id && activeUser) {
                setActiveUser({ ...activeUser, phase: r.phase } as V1User);
              }
            }}
          />
        </div>

        {error && (
          <Card dark={dark} style={{ padding: 10, borderColor: '#f04438' }}>
            <div style={{ fontSize: 13, color: '#b42318', fontWeight: 500 }}>{error}</div>
          </Card>
        )}
      </div>

      {/* Hauptbereich */}
      <div style={{ flex: 1, minHeight: 0 }}>
        {adding ? (
          <div style={{ maxWidth: 900 }}>
            <div style={{ fontSize: 16, fontWeight: 700, color: t.heading, marginBottom: 14 }}>
              {tr('pflege.familienkonto.detail.add_member_title')}
            </div>
            <FamilyMemberForm
              accountId={effectiveAccountId!}
              member={null}
              dark={dark}
              onSaved={handleMemberAdded}
              onCancel={() => setAdding(false)}
            />
          </div>
        ) : activeMember == null ? (
          <Card dark={dark} style={{ padding: 24 }}>
            <div style={{ fontSize: 13, color: t.muted }}>{tr('pflege.familienkonto.detail.no_members')}</div>
            <div style={{ marginTop: 14 }}>
              <Button variant="primary" size="sm" dark={dark} icon={<Icon.Plus />} onClick={() => setAdding(true)}>
                {tr('pflege.familienkonto.detail.add_member')}
              </Button>
            </div>
          </Card>
        ) : userLoading && !activeUser ? (
          <div style={{ padding: 24, color: t.muted, fontSize: 13 }}>{tr('pflege.detail.loading')}</div>
        ) : (activeMember.is_pflegeempfaenger || activeMember.is_care_recipient || activeMember.user_id != null) ? (
          // Gleicher Profilaufbau für alle Personen (Sidebar + Tabs +
          // Dashboard). Bei einer Kontaktperson (kein Pflegeempfänger)
          // schaltet `careRecipient={false}` die betreuungsbezogenen Teile ab —
          // Tagesplan, Betreuungen, Medien & Dokumente, Bewertungen sowie
          // Personenangaben/Startdatum/Nettolohn und die Dashboard-Blöcke
          // Jahresübersicht + Betreuungen.
          // Key = Person, damit beim Wechsel ein frischer Zustand entsteht.
          <PbDetailPage
            key={activeMember.profile_id}
            user={activeUser}
            tab={activeTab}
            dark={dark}
            careRecipient={activeMember.is_pflegeempfaenger || activeMember.is_care_recipient || isCareRecipientType(activeMember.profiltyp, profiltypOptions)}
            isKunde={activeMember.is_kunde === true}
            onClose={onBack}
            onSelectTab={(pbTab: PbTabId) => {
              if (activeMember.profile_id != null && effectiveAccountId != null) {
                onSelectMemberTab(activeMember.user_id, effectiveAccountId, activeMember.profile_id, pbTab);
              }
            }}
            onSaved={u => { setActiveUser(u); void reloadAccount(); }}
          />
        ) : (
          // Ohne eigenen users-Datensatz (Alt-Kontakte) trägt das Profil keine
          // Tabs/Dashboard — dann bleibt das schlanke Personen-Formular die
          // einzige sinnvolle Ansicht.
          //
          // Die Verzweigung hängt bewusst am MITGLIED, nicht an `activeUser`:
          // solange dessen V1User noch lädt (oder das Laden fehlschlägt), wäre
          // ein Pflegeempfänger sonst kurzzeitig — bzw. dauerhaft — auf die
          // schlanke Kontakt-Sicht zurückgefallen.
          <div style={{ maxWidth: 900 }}>
            <FamilyMemberForm
              accountId={effectiveAccountId!}
              member={activeMember}
              dark={dark}
              onSaved={() => { void reloadAccount(); }}
            />
          </div>
        )}
      </div>

      {/* Bestätigungsdialog: Person löschen */}
      {deleteTarget && (
        <div
          onClick={() => !deleting && setDeleteTarget(null)}
          style={{ position: 'fixed', inset: 0, background: 'rgba(16,24,40,0.35)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
        >
          <div role="dialog" aria-modal="true" onClick={e => e.stopPropagation()}>
            <Card dark={dark} style={{ padding: 20, width: 380, maxWidth: '90vw' }}>
              <div style={{ fontSize: 15, fontWeight: 700, color: t.heading, marginBottom: 8 }}>
                {tr('pflege.familienkonto.detail.delete_member_title')}
              </div>
              <div style={{ fontSize: 13, color: t.text, marginBottom: 6 }}>
                {tr('pflege.familienkonto.detail.delete_member_confirm', {
                  name: deleteTarget.fullname || [deleteTarget.vorname, deleteTarget.nachname].filter(Boolean).join(' ') || `#${deleteTarget.profile_id}`,
                })}
              </div>
              <div style={{ fontSize: 12, color: t.muted, marginBottom: 14 }}>
                {tr('pflege.familienkonto.detail.delete_member_hint')}
              </div>
              {deleteError && <div style={{ fontSize: 12, color: '#b42318', marginBottom: 10 }}>{deleteError}</div>}
              <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                <Button variant="secondary" size="sm" dark={dark} disabled={deleting} onClick={() => setDeleteTarget(null)}>
                  {tr('common.action.cancel')}
                </Button>
                <Button variant="primary" size="sm" dark={dark} disabled={deleting} onClick={() => void confirmDelete()}>
                  {deleting ? '…' : tr('pflege.familienkonto.detail.delete_member')}
                </Button>
              </div>
            </Card>
          </div>
        </div>
      )}
    </div>
  );
}
